import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Brain, ArrowLeft, Database, Target, TrendingUp, AlertTriangle, Trophy, Zap, CheckCircle, XCircle, Shield, Crosshair } from 'lucide-react';
import { MobileBottomNav } from '@/components/MobileNav';

const API_URL = process.env.REACT_APP_BACKEND_URL.replace(/^http:\/\//i, 'https://');
const COLORS = ['#00BFA6', '#7B1FA2', '#4CAF50', '#FF9800', '#F44336', '#2196F3', '#E91E63', '#009688', '#FF5722', '#3F51B5'];

const AILearningPage = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [errors, setErrors] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [precision, setPrecision] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [o, a, l, e, s, p] = await Promise.all([
          fetch(`${API_URL}/api/ai-learning/overview`).then(r => r.json()),
          fetch(`${API_URL}/api/ai-learning/accuracy`).then(r => r.json()),
          fetch(`${API_URL}/api/ai-learning/leagues`).then(r => r.json()),
          fetch(`${API_URL}/api/ai-learning/error-patterns`).then(r => r.json()),
          fetch(`${API_URL}/api/ai-learning/sessions`).then(r => r.json()),
          fetch(`${API_URL}/api/ai-learning/precision-mode`).then(r => r.json()),
        ]);
        setOverview(o);
        setAccuracy(a);
        setLeagues(l.leagues || []);
        setErrors(e);
        setSessions(s.sessions || []);
        setPrecision(p);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#121212] flex items-center justify-center">
        <div className="animate-pulse text-[#00BFA6] flex items-center gap-2">
          <Brain className="w-6 h-6 animate-spin" /> KI-Gehirn wird geladen...
        </div>
      </div>
    );
  }

  const sportAccData = Object.entries(accuracy?.by_sport || {}).map(([sport, data]) => ({
    sport,
    accuracy: data.accuracy,
    hc_accuracy: data.high_conf_accuracy,
    total: data.total,
  }));

  const predTypeData = [];
  Object.entries(accuracy?.by_type || {}).forEach(([sport, types]) => {
    Object.entries(types).forEach(([ptype, data]) => {
      predTypeData.push({
        name: `${sport} ${ptype === 'result' ? 'Ergebnis' : ptype === 'over_under' ? 'Over/Under' : 'BTTS'}`,
        accuracy: data.accuracy,
        hc_accuracy: data.high_conf_accuracy,
        total: data.total,
      });
    });
  });

  const errorData = (errors?.error_types || []).map(e => ({
    name: e._id === 'btts_miss' ? 'BTTS Fehler' :
          e._id === 'narrow_margin' ? 'Knappe Niederlage' :
          e._id === 'ou_miss_high' ? 'Over ueberschaetzt' :
          e._id === 'ou_miss_low' ? 'Under ueberschaetzt' :
          e._id === 'general_miss' ? 'Allgemein' :
          e._id === 'high_scoring_upset' ? 'Torreiches Upset' :
          e._id === 'high_confidence_miss' ? 'Hohe Konfidenz falsch' :
          e._id === 'draw_missed' ? 'Unentschieden verpasst' : e._id,
    count: e.count,
  }));

  return (
    <div className="min-h-screen bg-[#121212] pb-24">
      <header className="bg-[#1E1E1E] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Brain className="w-6 h-6 text-[#00BFA6]" />
          <h1 className="text-xl font-bold text-white" data-testid="ai-learning-title">KI-Lern-Dashboard</h1>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-gradient-to-br from-[#00BFA6]/10 to-[#00BFA6]/5 border-[#00BFA6]/20">
            <CardContent className="pt-4 pb-4">
              <Database className="w-5 h-5 text-[#00BFA6] mb-2" />
              <div className="text-2xl font-bold text-white font-mono" data-testid="ai-overview-matches">{(overview?.matches || 0).toLocaleString()}</div>
              <div className="text-xs text-gray-400">Historische Matches</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-[#7B1FA2]/10 to-[#7B1FA2]/5 border-[#7B1FA2]/20">
            <CardContent className="pt-4 pb-4">
              <Brain className="w-5 h-5 text-[#7B1FA2] mb-2" />
              <div className="text-2xl font-bold text-white font-mono" data-testid="ai-overview-learning">{(overview?.learning_entries || 0).toLocaleString()}</div>
              <div className="text-xs text-gray-400">Lern-Eintraege</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-500/10 to-red-500/5 border-red-500/20">
            <CardContent className="pt-4 pb-4">
              <AlertTriangle className="w-5 h-5 text-red-400 mb-2" />
              <div className="text-2xl font-bold text-white font-mono">{(overview?.error_analyses || 0).toLocaleString()}</div>
              <div className="text-xs text-gray-400">Fehler analysiert</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-[#4CAF50]/10 to-[#4CAF50]/5 border-[#4CAF50]/20">
            <CardContent className="pt-4 pb-4">
              <Zap className="w-5 h-5 text-[#4CAF50] mb-2" />
              <div className="text-2xl font-bold text-[#4CAF50] font-mono" data-testid="ai-overview-datapoints">{(overview?.data_points || 0).toLocaleString()}</div>
              <div className="text-xs text-gray-400">Datenpunkte</div>
            </CardContent>
          </Card>
        </div>

        {/* Secondary stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-[#1E1E1E] border-white/10">
            <CardContent className="pt-3 pb-3 text-center">
              <div className="text-lg font-bold text-white font-mono">{(overview?.teams || 0).toLocaleString()}</div>
              <div className="text-[10px] text-gray-500">Teams</div>
            </CardContent>
          </Card>
          <Card className="bg-[#1E1E1E] border-white/10">
            <CardContent className="pt-3 pb-3 text-center">
              <div className="text-lg font-bold text-white font-mono">{(overview?.players || 0).toLocaleString()}</div>
              <div className="text-[10px] text-gray-500">Spieler</div>
            </CardContent>
          </Card>
          <Card className="bg-[#1E1E1E] border-white/10">
            <CardContent className="pt-3 pb-3 text-center">
              <div className="text-lg font-bold text-[#00BFA6] font-mono">{(overview?.elo_ratings || 0).toLocaleString()}</div>
              <div className="text-[10px] text-gray-500">Elo-Ratings</div>
            </CardContent>
          </Card>
          <Card className="bg-[#1E1E1E] border-white/10">
            <CardContent className="pt-3 pb-3 text-center">
              <div className="text-lg font-bold text-white font-mono">{(overview?.standings || 0).toLocaleString()}</div>
              <div className="text-[10px] text-gray-500">Tabellen-Eintraege</div>
            </CardContent>
          </Card>
        </div>

        {/* Model Version Badge */}
        <div className="flex items-center gap-2 px-3 py-2 bg-[#1E1E1E] rounded-lg border border-white/5">
          <Zap className="w-4 h-4 text-[#00BFA6]" />
          <span className="text-xs text-gray-400">Modell: <span className="text-white font-medium">{overview?.model_version || 'Elite Elo v4.0'}</span></span>
          {overview?.last_training && (
            <span className="text-[10px] text-gray-600 ml-auto">Letztes Training: {new Date(overview.last_training).toLocaleDateString('de-DE')}</span>
          )}
        </div>

        {/* PRECISION MODE HERO */}
        {precision && (
          <div data-testid="precision-mode-hero" className="relative overflow-hidden rounded-2xl border border-[#00BFA6]/30 bg-gradient-to-br from-[#00BFA6]/10 via-[#121212] to-[#7B1FA2]/10">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIxIiBmaWxsPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDMpIi8+PC9zdmc+')] opacity-50" />
            <div className="relative p-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#00BFA6]/20 flex items-center justify-center">
                  <Crosshair className="w-5 h-5 text-[#00BFA6]" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Precision Mode</h2>
                  <p className="text-xs text-gray-400">{precision.mode} — Nur High-Confidence Tipps</p>
                </div>
              </div>

              <div className="flex items-end gap-2 mb-4">
                <span data-testid="precision-global-accuracy" className="text-5xl font-black font-mono text-[#00BFA6] leading-none">
                  {precision.global_precision}%
                </span>
                <span className="text-sm text-gray-400 pb-1">Praezision</span>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                Basierend auf {(precision.global_precision_tips || 0).toLocaleString()} gefilterten High-Confidence Vorhersagen
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {(precision.precision_stats || []).map((ps, i) => (
                  <div key={i} data-testid={`precision-sport-${ps.sport}`} className="p-3 bg-black/30 rounded-xl border border-white/5">
                    <div className="text-xs text-gray-400 mb-1">{ps.sport}</div>
                    <div className="flex items-end gap-2">
                      <span className="text-2xl font-bold font-mono text-white">{ps.best_precision}%</span>
                      <span className="text-[10px] text-gray-500 pb-0.5">{ps.best_precision_tips} Tipps</span>
                    </div>
                    <div className="mt-1.5 w-full bg-white/5 rounded-full h-1.5">
                      <div className="h-1.5 rounded-full bg-[#00BFA6] transition-all" style={{ width: `${Math.min(ps.best_precision, 100)}%` }} />
                    </div>
                    <div className="flex justify-between text-[9px] text-gray-600 mt-1">
                      <span>Gesamt: {ps.overall_accuracy}%</span>
                      <span>HC: {ps.high_confidence_accuracy}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <Tabs defaultValue="accuracy" className="w-full">
          <TabsList className="bg-[#1E1E1E] border border-white/10 flex flex-wrap">
            <TabsTrigger value="accuracy" data-testid="tab-accuracy">Genauigkeit</TabsTrigger>
            <TabsTrigger value="leagues" data-testid="tab-leagues">Ligen</TabsTrigger>
            <TabsTrigger value="errors" data-testid="tab-errors">Fehleranalyse</TabsTrigger>
            <TabsTrigger value="sessions" data-testid="tab-sessions">Training</TabsTrigger>
          </TabsList>

          {/* Accuracy Tab */}
          <TabsContent value="accuracy" className="space-y-6 mt-4">
            {predTypeData.length > 0 && (
              <Card className="bg-[#1E1E1E] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Target className="w-5 h-5 text-[#00BFA6]" />
                    Genauigkeit nach Vorhersage-Typ
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={predTypeData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="name" stroke="#999" tick={{ fontSize: 10 }} />
                      <YAxis stroke="#999" domain={[0, 100]} />
                      <Tooltip contentStyle={{ backgroundColor: '#1E1E1E', border: '1px solid rgba(255,255,255,0.1)' }}
                        formatter={(value, name) => [`${value}%`, name === 'accuracy' ? 'Gesamt' : 'High Confidence']} />
                      <Bar dataKey="accuracy" fill="#00BFA6" name="Gesamt" />
                      <Bar dataKey="hc_accuracy" fill="#7B1FA2" name="High Confidence" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {sportAccData.length > 0 && (
              <Card className="bg-[#1E1E1E] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white">Genauigkeit nach Sportart</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {sportAccData.map((s, i) => (
                      <div key={i} data-testid={`accuracy-sport-${i}`} className="p-4 bg-[#121212] rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-white font-medium">{s.sport}</span>
                          <span className="text-[#00BFA6] font-bold font-mono">{s.accuracy}%</span>
                        </div>
                        <div className="w-full bg-white/5 rounded-full h-2">
                          <div
                            className="h-2 rounded-full transition-all duration-500"
                            style={{
                              width: `${s.accuracy}%`,
                              backgroundColor: s.accuracy >= 60 ? '#4CAF50' : s.accuracy >= 50 ? '#FF9800' : '#F44336'
                            }}
                          />
                        </div>
                        <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                          <span>{s.total.toLocaleString()} Tips</span>
                          <span>HC: {s.hc_accuracy}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Leagues Tab */}
          <TabsContent value="leagues" className="mt-4">
            <Card className="bg-[#1E1E1E] border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-400" />
                  Genauigkeit nach Liga (Top 30)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {leagues.length === 0 ? (
                  <div className="text-gray-500 text-center py-8">Noch keine Liga-Daten</div>
                ) : (
                  <div className="space-y-2">
                    {leagues.slice(0, 30).map((l, i) => (
                      <div key={i} data-testid={`league-acc-${i}`} className="flex items-center justify-between p-3 bg-[#121212] rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className={`w-7 text-center text-sm font-bold ${i < 3 ? 'text-yellow-400' : 'text-gray-500'}`}>
                            {i + 1}
                          </span>
                          <div>
                            <div className="text-white text-sm font-medium">{l.league}</div>
                            <div className="text-[10px] text-gray-500">{l.total} Tips analysiert</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-20 bg-white/5 rounded-full h-1.5">
                            <div
                              className="h-1.5 rounded-full"
                              style={{
                                width: `${l.accuracy}%`,
                                backgroundColor: l.accuracy >= 60 ? '#4CAF50' : l.accuracy >= 50 ? '#FF9800' : '#F44336'
                              }}
                            />
                          </div>
                          <span className="text-[#00BFA6] font-mono font-bold text-sm w-12 text-right">{l.accuracy}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Errors Tab */}
          <TabsContent value="errors" className="space-y-6 mt-4">
            <Card className="bg-[#1E1E1E] border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  Wo irrt sich die KI?
                </CardTitle>
              </CardHeader>
              <CardContent>
                {errorData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={errorData}
                        dataKey="count"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={110}
                        label={({ name, count }) => `${name}: ${count}`}
                        labelLine={{ stroke: '#666' }}
                      >
                        {errorData.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: '#1E1E1E', border: '1px solid rgba(255,255,255,0.1)' }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-gray-500 text-center py-8">Noch keine Fehleranalysen</div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-[#1E1E1E] border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-base">Was bedeuten die Fehlertypen?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="flex gap-3 p-3 bg-[#121212] rounded-lg">
                    <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-white font-medium">Knappe Niederlage</div>
                      <div className="text-gray-500 text-xs">Das vorhergesagte Team hat knapp verloren (1 Tor Differenz)</div>
                    </div>
                  </div>
                  <div className="flex gap-3 p-3 bg-[#121212] rounded-lg">
                    <XCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-white font-medium">Over ueberschaetzt</div>
                      <div className="text-gray-500 text-xs">Die KI hat mehr Tore erwartet als gefallen sind</div>
                    </div>
                  </div>
                  <div className="flex gap-3 p-3 bg-[#121212] rounded-lg">
                    <XCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-white font-medium">Hohe Konfidenz falsch</div>
                      <div className="text-gray-500 text-xs">Die KI war sich sehr sicher, lag aber trotzdem falsch — wird besonders stark gewichtet beim Lernen</div>
                    </div>
                  </div>
                  <div className="flex gap-3 p-3 bg-[#121212] rounded-lg">
                    <CheckCircle className="w-5 h-5 text-[#00BFA6] flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-white font-medium">Selbstlernendes System</div>
                      <div className="text-gray-500 text-xs">Die KI analysiert jeden Fehler und passt ihre Gewichtungen automatisch an, um beim naechsten Mal besser zu sein</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sessions Tab */}
          <TabsContent value="sessions" className="mt-4">
            <Card className="bg-[#1E1E1E] border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Brain className="w-5 h-5 text-[#00BFA6]" />
                  Training-Sessions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {sessions.length === 0 ? (
                  <div className="text-gray-500 text-center py-8">Noch keine Sessions</div>
                ) : (
                  <div className="space-y-3">
                    {sessions.map((s, i) => (
                      <div key={i} data-testid={`session-${i}`} className="p-4 bg-[#121212] rounded-lg border border-white/5">
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-white font-medium text-sm">{s.session_id}</span>
                          <span className="text-xs text-gray-500">{s.completed_at ? new Date(s.completed_at).toLocaleString('de-DE') : ''}</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div>
                            <div className="text-[10px] text-gray-500">Validiert</div>
                            <div className="text-white font-mono font-bold">{(s.predictions_validated || 0).toLocaleString()}</div>
                          </div>
                          <div>
                            <div className="text-[10px] text-gray-500">Korrekt</div>
                            <div className="text-green-400 font-mono font-bold">{(s.correct_predictions || 0).toLocaleString()}</div>
                          </div>
                          <div>
                            <div className="text-[10px] text-gray-500">Genauigkeit</div>
                            <div className="text-[#00BFA6] font-mono font-bold">{s.session_accuracy || 0}%</div>
                          </div>
                          <div>
                            <div className="text-[10px] text-gray-500">Dauer</div>
                            <div className="text-white font-mono">{s.duration_seconds ? `${Math.round(s.duration_seconds)}s` : '-'}</div>
                          </div>
                        </div>
                        {s.matches_processed && (
                          <div className="mt-2 text-[10px] text-gray-500">
                            {s.matches_processed.toLocaleString()} Matches verarbeitet | {(s.teams_tracked || 0).toLocaleString()} Teams getrackt
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      <MobileBottomNav />
    </div>
  );
};

export default AILearningPage;
